import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { buildUrl, createRequest } from './common/httpUtils'
import { appendAuthToken } from './common/session'
import { retrieveResponseErrorMessage } from '../utils/errorUtils'
import type { RootState } from '../store'
import type { QrTagEventTypeEntry } from '../constants/Constants'

type GroupEventTypesEntry = {
  values: QrTagEventTypeEntry[]
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
  fetchedAt: number | null
}

type GroupEventTypesState = {
  entries: Record<GroupId, GroupEventTypesEntry | undefined>
}

const initialState: GroupEventTypesState = {
  entries: {},
}

type FetchGroupEventTypesArgs = {
  groupId: GroupId
  force?: boolean
}

type FetchGroupEventTypesResult = {
  groupId: GroupId
  eventTypes: QrTagEventTypeEntry[]
}

type FetchGroupEventTypesError = {
  groupId: GroupId
  error: string
}

type CreateGroupEventTypeArgs = {
  groupId: GroupId
  name: string
  color?: string | null
  position?: number | null
}

type CreateGroupEventTypeResult = {
  groupId: GroupId
  eventType: QrTagEventTypeEntry
}

type CreateGroupEventTypeError = {
  groupId: GroupId
  error: string
}

type UpdateGroupEventTypeArgs = {
  groupId: GroupId
  id: number
  name?: string
  color?: string | null
  position?: number
}

type UpdateGroupEventTypeResult = {
  groupId: GroupId
  eventType: QrTagEventTypeEntry
}

type UpdateGroupEventTypeError = {
  groupId: GroupId
  error: string
}

type DeleteGroupEventTypeArgs = {
  groupId: GroupId
  id: number
}

type DeleteGroupEventTypeResult = {
  groupId: GroupId
  id: number
}

type DeleteGroupEventTypeError = {
  groupId: GroupId
  error: string
}

const normalizeEventTypes = (raw: unknown): QrTagEventTypeEntry[] => {
  if (!Array.isArray(raw)) return []

  return raw
    .map((item) => {
      if (item && typeof item === 'object') {
        const maybeId = (item as { id?: unknown }).id
        const id = typeof maybeId === 'number' ? maybeId : Number(maybeId)
        if (!Number.isFinite(id)) return null
        const maybeName = (item as { name?: unknown }).name
        const name = typeof maybeName === 'string' ? maybeName.trim() : ''
        if (name.length === 0) return null
        const maybeColor = (item as { color?: unknown }).color
        const colorValue = typeof maybeColor === 'string' ? maybeColor.trim() : ''
        const maybePosition = (item as { position?: unknown }).position
        const position = typeof maybePosition === 'number' ? maybePosition : Number(maybePosition)
        return {
          id,
          name,
          color: colorValue.length > 0 ? colorValue : null,
          position: Number.isFinite(position) ? position : 0,
        }
      }

      return null
    })
    .filter((item): item is QrTagEventTypeEntry => Boolean(item))
}

const sortEventTypes = (eventTypes: QrTagEventTypeEntry[]): QrTagEventTypeEntry[] => (
  [...eventTypes].sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position
    return a.name.localeCompare(b.name)
  })
)

export const fetchGroupEventTypes = createAsyncThunk<
FetchGroupEventTypesResult,
FetchGroupEventTypesArgs,
{
  rejectValue: FetchGroupEventTypesError
  state: RootState
}
>(
  'qrTagEventTypes/fetchGroupEventTypes',
  async ({ groupId }, thunkApi) => {
    const { rejectWithValue } = thunkApi

    try {
      const url = buildUrl(appendAuthToken(`/api/v1/groups/${groupId}/qr_tag_event_types`))
      const request = createRequest(url, 'GET')
      const response = await fetch(request)
      const data = await response.json()

      if (!response.ok) {
        const message = data?.error || data?.error_message || response.statusText
        return rejectWithValue({
          groupId,
          error: message || 'イベントタイプの取得に失敗しました。',
        })
      }

      return {
        groupId,
        eventTypes: sortEventTypes(normalizeEventTypes(data?.event_types)),
      }
    } catch (error: unknown) {
      return rejectWithValue({
        groupId,
        error: retrieveResponseErrorMessage(error),
      })
    }
  },
  {
    condition: ({ groupId, force }: FetchGroupEventTypesArgs, { getState }) => {
      if (force) return true
      const state = getState() as RootState
      const entry = state.qrTagEventTypes.entries[groupId]
      if (!entry) return true
      if (entry.status === 'loading') return false
      return entry.status !== 'succeeded'
    },
  },
)

export const createGroupEventType = createAsyncThunk<
CreateGroupEventTypeResult,
CreateGroupEventTypeArgs,
{
  rejectValue: CreateGroupEventTypeError
}
>(
  'qrTagEventTypes/createGroupEventType',
  async ({
    groupId,
    name,
    color,
    position,
  }, thunkApi) => {
    const { rejectWithValue } = thunkApi

    try {
      const url = buildUrl(appendAuthToken(`/api/v1/groups/${groupId}/qr_tag_event_types`))
      const request = createRequest(
        url,
        'POST',
        true,
        'application/json',
        {
          event_type: {
            name,
            color,
            position,
          },
        },
      )
      const response = await fetch(request)
      const data = await response.json()

      if (!response.ok) {
        const message = data?.error || data?.error_message || response.statusText
        return rejectWithValue({
          groupId,
          error: message || 'イベントタイプの作成に失敗しました。',
        })
      }

      const [eventType] = normalizeEventTypes([data?.event_type])
      if (!eventType) {
        return rejectWithValue({
          groupId,
          error: 'イベントタイプの作成に失敗しました。',
        })
      }

      return {
        groupId,
        eventType,
      }
    } catch (error: unknown) {
      return rejectWithValue({
        groupId,
        error: retrieveResponseErrorMessage(error),
      })
    }
  },
)

export const updateGroupEventType = createAsyncThunk<
UpdateGroupEventTypeResult,
UpdateGroupEventTypeArgs,
{
  rejectValue: UpdateGroupEventTypeError
}
>(
  'qrTagEventTypes/updateGroupEventType',
  async ({
    groupId,
    id,
    name,
    color,
    position,
  }, thunkApi) => {
    const { rejectWithValue } = thunkApi

    try {
      const url = buildUrl(appendAuthToken(`/api/v1/qr_tag_event_types/${id}`))
      const request = createRequest(
        url,
        'PATCH',
        true,
        'application/json',
        {
          event_type: {
            name,
            color,
            position,
          },
        },
      )
      const response = await fetch(request)
      const data = await response.json()

      if (!response.ok) {
        const message = data?.error || data?.error_message || response.statusText
        return rejectWithValue({
          groupId,
          error: message || 'イベントタイプの更新に失敗しました。',
        })
      }

      const [eventType] = normalizeEventTypes([data?.event_type])
      if (!eventType) {
        return rejectWithValue({
          groupId,
          error: 'イベントタイプの更新に失敗しました。',
        })
      }

      return {
        groupId,
        eventType,
      }
    } catch (error: unknown) {
      return rejectWithValue({
        groupId,
        error: retrieveResponseErrorMessage(error),
      })
    }
  },
)

export const deleteGroupEventType = createAsyncThunk<
DeleteGroupEventTypeResult,
DeleteGroupEventTypeArgs,
{
  rejectValue: DeleteGroupEventTypeError
}
>(
  'qrTagEventTypes/deleteGroupEventType',
  async ({ groupId, id }, thunkApi) => {
    const { rejectWithValue } = thunkApi

    try {
      const url = buildUrl(appendAuthToken(`/api/v1/qr_tag_event_types/${id}`))
      const request = createRequest(url, 'DELETE')
      const response = await fetch(request)
      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        const message = data?.error || data?.error_message || response.statusText
        return rejectWithValue({
          groupId,
          error: message || 'イベントタイプの削除に失敗しました。',
        })
      }

      return {
        groupId,
        id,
      }
    } catch (error: unknown) {
      return rejectWithValue({
        groupId,
        error: retrieveResponseErrorMessage(error),
      })
    }
  },
)

const upsertEntry = (
  state: GroupEventTypesState,
  groupId: GroupId,
  entry: GroupEventTypesEntry,
): GroupEventTypesState => ({
  ...state,
  entries: {
    ...state.entries,
    [groupId]: entry,
  },
})

const qrTagEventTypesSlice = createSlice({
  name: 'qrTagEventTypes',
  initialState,
  reducers: {
    clearGroupEventTypes: (state, action: PayloadAction<{ groupId: GroupId }>) => {
      const entries = { ...state.entries }
      delete entries[action.payload.groupId]
      return {
        ...state,
        entries,
      }
    },
    setGroupEventTypes: (state, action: PayloadAction<{ groupId: GroupId, eventTypes: QrTagEventTypeEntry[] }>) => (
      upsertEntry(state, action.payload.groupId, {
        values: sortEventTypes(action.payload.eventTypes),
        status: 'succeeded',
        error: null,
        fetchedAt: Date.now(),
      })
    ),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroupEventTypes.pending, (state, action) => {
        const { groupId } = action.meta.arg
        const existing = state.entries[groupId]
        return upsertEntry(state, groupId, {
          values: existing?.values ?? [],
          status: 'loading',
          error: null,
          fetchedAt: existing?.fetchedAt ?? null,
        })
      })
      .addCase(fetchGroupEventTypes.fulfilled, (state, action) => {
        const { groupId, eventTypes } = action.payload
        return upsertEntry(state, groupId, {
          values: sortEventTypes(eventTypes),
          status: 'succeeded',
          error: null,
          fetchedAt: Date.now(),
        })
      })
      .addCase(fetchGroupEventTypes.rejected, (state, action) => {
        const { groupId } = action.meta.arg
        const errorMessage = action.payload?.error || action.error.message || 'イベントタイプの取得に失敗しました。'
        return upsertEntry(state, groupId, {
          values: [],
          status: 'failed',
          error: errorMessage,
          fetchedAt: Date.now(),
        })
      })
      .addCase(createGroupEventType.fulfilled, (state, action) => {
        const { groupId, eventType } = action.payload
        const existing = state.entries[groupId]
        const nextValues = sortEventTypes([...(existing?.values ?? []), eventType])
        return upsertEntry(state, groupId, {
          values: nextValues,
          status: 'succeeded',
          error: null,
          fetchedAt: Date.now(),
        })
      })
      .addCase(updateGroupEventType.fulfilled, (state, action) => {
        const { groupId, eventType } = action.payload
        const existing = state.entries[groupId]
        const nextValues = sortEventTypes((existing?.values ?? []).map((entry) => (
          entry.id === eventType.id ? eventType : entry
        )))
        return upsertEntry(state, groupId, {
          values: nextValues,
          status: 'succeeded',
          error: null,
          fetchedAt: Date.now(),
        })
      })
      .addCase(deleteGroupEventType.fulfilled, (state, action) => {
        const { groupId, id } = action.payload
        const existing = state.entries[groupId]
        const nextValues = (existing?.values ?? []).filter((entry) => entry.id !== id)
        return upsertEntry(state, groupId, {
          values: sortEventTypes(nextValues),
          status: 'succeeded',
          error: null,
          fetchedAt: Date.now(),
        })
      })
  },
})

export const selectGroupEventTypesEntry = (state: RootState, groupId: GroupId) => (
  state.qrTagEventTypes.entries[groupId]
)

export const selectGroupEventTypes = (state: RootState, groupId: GroupId) => (
  selectGroupEventTypesEntry(state, groupId)?.values ?? []
)

export const {
  clearGroupEventTypes,
  setGroupEventTypes,
} = qrTagEventTypesSlice.actions

export default qrTagEventTypesSlice.reducer
